/**
 * AdminSidebar - Collapsible left sidebar for admin panel (desktop).
 *
 * Dikey 11 öğeli admin navigasyonu. Kullanıcı ok butonuyla sidebar'ı
 * daraltıp genişletebilir; tercih localStorage'da saklanır. Mobilde
 * render edilmez (Layout.jsx'teki mevcut Sheet drawer kullanılır).
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import {
  LayoutDashboard,
  Users,
  ClipboardList,
  Package,
  ShoppingBag,
  ShoppingCart,
  UserCheck,
  BarChart3,
  Inbox,
  Target,
  FileText,
  ChevronLeft,
  ChevronRight,
  Warehouse,
  ArrowDownToLine,
  ArrowUpFromLine,
  Calendar,
  ClipboardCheck,
  ListOrdered,
  Truck,
  Landmark,
  BookOpen,
  ListTree,
  Scale,
  Wallet,
  Factory,
  CalendarCheck,
  Receipt,
  BellRing,
  FilePlus2,
  Recycle,
  ScanLine,
  Repeat,
  Plug,
  Store, Settings2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const STORAGE_KEY = 'inmotion_admin_sidebar_collapsed';

/**
 * Admin navigasyonu — TEK KAYNAK. Hem masaüstü sidebar hem mobil drawer
 * (Layout.jsx) bu tanımı kullanır → tutarlılık garanti.
 * Lager grubu warehouse route'larına gider; admin bu route'lara
 * ProtectedRoute bypass'ı ile erişebilir.
 */
export const ADMIN_SECTIONS = [
  {
    label: 'Vertrieb',
    items: [
      { name: 'AdminDashboard', label: 'Dashboard', icon: LayoutDashboard },
      { name: 'AdminLeads', label: 'Kontakte', icon: Users },
      { name: 'AdminTasks', label: 'Aufgaben', icon: ClipboardList },
      { name: 'AdminOrders', label: 'Aufträge', icon: Package },
      { name: 'AdminInbox', label: 'Angebote', icon: Inbox },
      { name: 'AdminShopifyOrders', label: 'Shopify', icon: ShoppingCart },
    ],
  },
  {
    label: 'Lager',
    items: [
      { name: 'WarehouseWareneingang', label: 'Wareneingang', icon: ArrowDownToLine },
      { name: 'WarehouseWarenausgang', label: 'Warenausgang', icon: ArrowUpFromLine },
      { name: 'WarehouseDispo', label: 'Dispo', icon: Calendar },
      { name: 'WarehouseDashboard', label: 'Depo', icon: Warehouse },
      { name: 'WarehouseInventur', label: 'Inventur', icon: ClipboardCheck },
    ],
  },
  {
    label: 'Buchhaltung',
    items: [
      { name: 'BuchhaltungRechnungen', label: 'Rechnungen', icon: Receipt },
      { name: 'BuchhaltungMahnungen', label: 'Mahnungen', icon: BellRing },
      { name: 'BuchhaltungGutschriften', label: 'Gutschriften', icon: FilePlus2 },
      { name: 'BuchhaltungAusgaben', label: 'Ausgaben', icon: ScanLine },
      { name: 'BuchhaltungWiederkehrend', label: 'Wiederkehrend', icon: Repeat },
      { name: 'BuchhaltungAnlagen', label: 'Anlagen & AfA', icon: Factory },
      { name: 'BuchhaltungDashboard', label: 'Finanzen', icon: Landmark },
      { name: 'BuchhaltungJournal', label: 'Journal', icon: BookOpen },
      { name: 'BuchhaltungKonten', label: 'Kontenplan', icon: ListTree },
      { name: 'BuchhaltungBerichte', label: 'Fibu-Berichte', icon: Scale },
      { name: 'BuchhaltungBank', label: 'Bank & Zahlungen', icon: Wallet },
      { name: 'BuchhaltungAbschluss', label: 'Jahresabschluss', icon: CalendarCheck },
    ],
  },
  {
    label: 'Daten',
    items: [
      { name: 'AdminProducts', label: 'Produkte', icon: ShoppingBag },
      { name: 'AdminStockMovements', label: 'Bewegungen', icon: ListOrdered },
      { name: 'AdminSuppliers', label: 'Lieferanten', icon: Truck },
      { name: 'AdminAgents', label: 'Agenten', icon: UserCheck },
    ],
  },
  {
    label: 'Einblicke',
    items: [
      { name: 'AdminReports', label: 'Berichte', icon: BarChart3 },
      { name: 'AdminAgentPerformance', label: 'Performance', icon: BarChart3 },
      { name: 'AdminLeadHunter', label: 'Lead Hunter', icon: Target },
    ],
  },
  {
    label: 'System',
    items: [
      { name: 'AdminPdfSettings', label: 'PDF-Einstellungen', icon: FileText },
      { name: 'AdminEwkKategorien', label: 'EWK-Kategorien', icon: Recycle },
      { name: 'AdminKundenportal', label: 'Kundenportal', icon: Store },
      { name: 'AdminIntegrationen', label: 'Integrationen', icon: Plug },
      { name: 'AdminEinstellungen', label: 'Einstellungen', icon: Settings2 },
    ],
  },
];

// Geriye dönük uyumluluk için eski isim
const SECTIONS = ADMIN_SECTIONS;

export function useAdminSidebarCollapsed() {
  const [collapsed, setCollapsed] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  React.useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignore quota errors */
    }
  }, [collapsed]);

  return [collapsed, setCollapsed];
}

export default function AdminSidebar({ activePageName, collapsed, onToggle }) {
  const location = useLocation();

  return (
    <aside
      className={cn(
        'hidden md:flex fixed top-14 bottom-0 left-0 z-40 bg-brand-950 text-white border-r border-white/5 flex-col transition-[width] duration-200 ease-out',
        collapsed ? 'w-16' : 'w-60'
      )}
    >
      <div className="flex items-center justify-end px-2 py-2 border-b border-white/5">
        <button
          type="button"
          onClick={() => onToggle(!collapsed)}
          className="p-1.5 rounded-md text-white/50 hover:text-white hover:bg-white/10 transition-colors"
          aria-label={collapsed ? 'Sidebar erweitern' : 'Sidebar einklappen'}
          title={collapsed ? 'Erweitern' : 'Einklappen'}
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-2">
        {SECTIONS.map((section) => (
          <div key={section.label} className="mb-3">
            {!collapsed && (
              <p className="px-4 mb-1 text-[10px] font-bold uppercase tracking-wider text-white/40">
                {section.label}
              </p>
            )}
            <ul>
              {section.items.map((item) => {
                const Icon = item.icon;
                const href = createPageUrl(item.name);
                const isActive = activePageName === item.name || location.pathname.startsWith(href);
                return (
                  <li key={item.name}>
                    <Link
                      to={href}
                      title={collapsed ? item.label : undefined}
                      className={cn(
                        'flex items-center gap-3 mx-2 my-0.5 px-2.5 py-2 rounded-md text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-white/10 text-amber-400'
                          : 'text-white/70 hover:text-white hover:bg-white/5',
                        collapsed && 'justify-center'
                      )}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={isActive ? 2.5 : 2} />
                      {!collapsed && <span className="truncate">{item.label}</span>}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
          <div className="px-3 py-2 text-[10px] text-white/30 font-mono" title="Build-Version">v {__BUILD_ID__}</div>
</aside>
  );
}
